import { confirmSignIn, fetchUserAttributes, getCurrentUser, resendSignUpCode, signIn, signOut } from "aws-amplify/auth";
import errorHandler from "common/errorHandler";
import { call, join, put, select, spawn, takeEvery, takeLatest } from "redux-saga/effects";
import { Endpoints } from "services/api";
import * as globalActions from "../actions";
import * as actions from "./actions";

const getTenant = (state) => state.userLogin.tenant;
const getLoggedInUser = (state) => state.userLogin.user;

function* getUserTenant(api) {
    try {
        let result = yield call([api, api.get], `${Endpoints.tenants}/api/v1/tenant`);
        return result.data;
    } catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* getUser(api, userId, tenantId) {
    try {
        let result = yield call([api, api.get], `${Endpoints.users}/api/v1/users/${userId}`, { tenantid: tenantId });
        return result.data;
    } catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

export function* userLogin(api, { email, password }) {
    try {
        let signInResult = yield call(signIn, { username: email, password });

        if (signInResult.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
            yield put(actions.userLoginFailure({ code: "CHANGE_TEMPORARY_PASSWORD", message: "Change temporary password", email }));
            return;
        }

        // Get current user and attributes
        let currentUser = yield call(getCurrentUser);
        let userAttributes = yield call(fetchUserAttributes);

        // Create a cognitoUser-like object for backwards compatibility
        let cognitoUser = {
            username: currentUser.username,
            userId: currentUser.userId,
            attributes: {
                sub: userAttributes.sub,
                email: userAttributes.email,
                name: userAttributes.name,
                family_name: userAttributes.family_name,
                "custom:tenantId": userAttributes["custom:tenantId"],
            },
        };

        let newTenant = false;

        let userTenantTask = yield spawn(getUserTenant, api);
        let currentUserTask = yield spawn(getUser, api, cognitoUser.attributes.sub, cognitoUser.attributes["custom:tenantId"]);

        let userTenant = yield join(userTenantTask);
        if (userTenant) {
            if (userTenant.state === "ACTIVE") {
                let currentUserData = yield join(currentUserTask);
                if (!currentUserData) {
                    yield put(actions.userLoginFailure({ code: "PERMISSION_DENIED", message: "Permission denied. Please contact your account administrator." }));
                    return;
                } else {
                    yield put(actions.userLoginSuccess({ cognitoUser, user: currentUserData, tenant: userTenant, newTenant }));
                }
            } else {
                yield put(actions.userLoginSuccess({ cognitoUser, user: cognitoUser, tenant: userTenant, newTenant }));
            }
        } else {
            yield put(actions.userLoginFailure({ code: "PERMISSION_DENIED", message: "Permission denied. Please contact your account administrator." }));
        }
    } catch (error) {
        yield call(signOut);
        let errorObject = errorHandler(error, "login", "user");
        yield put(actions.userLoginFailure(errorObject));
    }
}


export function* userReauth(api, { email, password }) {
    try {
        const existingTenant = yield select(getTenant);
        const existingUser = yield select(getLoggedInUser);

        yield call(signOut);

        let signInResult = yield call(signIn, { username: email, password });

        if (signInResult.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
            yield put(actions.userLoginFailure({ code: "CHANGE_TEMPORARY_PASSWORD", message: "Change temporary password", email }));
            return;
        }

        let currentUser = yield call(getCurrentUser);
        let userAttributes = yield call(fetchUserAttributes);

        let cognitoUser = {
            username: currentUser.username,
            userId: currentUser.userId,
            attributes: {
                sub: userAttributes.sub,
                email: userAttributes.email,
                name: userAttributes.name,
                family_name: userAttributes.family_name,
                "custom:tenantId": userAttributes["custom:tenantId"],
            },
        };

        yield put(
            actions.userLoginSuccess({
                cognitoUser,
                user: existingUser,
                tenant: existingTenant,
                newTenant: false,
            }),
        );
    } catch (error) {
        let errorObject = errorHandler(error, "login", "user");
        yield put(actions.userLoginFailure(errorObject));
    }
}

export function* getUserAndTenant(api) {
    let tenant = null;
    let user = null;
    try {
        tenant = yield select(getTenant);
        user = yield select(getLoggedInUser);

        let currentUser = yield call(getCurrentUser);
        let userAttributes = yield call(fetchUserAttributes);

        if (currentUser) {
            // Create a cognitoUser-like object for backwards compatibility
            let cognitoUser = {
                username: currentUser.username,
                userId: currentUser.userId,
                attributes: {
                    sub: userAttributes.sub,
                    email: userAttributes.email,
                    name: userAttributes.name,
                    family_name: userAttributes.family_name,
                    "custom:tenantId": userAttributes["custom:tenantId"],
                },
            };

            let userTenantTask = yield spawn(getUserTenant, api);
            let currentUserTask = yield spawn(getUser, api, cognitoUser.attributes.sub, cognitoUser.attributes["custom:tenantId"]);

            let userTenant = yield join(userTenantTask);
            if (userTenant) {
                tenant = userTenant;
                if (userTenant.state === "ACTIVE") {
                    let currentUserData = yield join(currentUserTask);
                    if (!currentUserData) {
                        yield put(actions.getUserAndTenantFailure({ code: "failed", message: "Get user failed" }));
                        return;
                    } else {
                        user = currentUserData;
                        yield put(globalActions.registerNotificationRequest(userTenant.tenantId, currentUserData.userId));
                        yield put(actions.getUserAndTenantSuccess({ cognitoUser, user: currentUserData, tenant: userTenant }));
                    }
                } else {
                    yield put(globalActions.registerNotificationRequest(userTenant.tenantId, cognitoUser.attributes.sub));
                    yield put(actions.getUserAndTenantSuccess({ cognitoUser, user: cognitoUser, tenant: userTenant }));
                }
            } else {
                yield put(actions.getUserAndTenantFailure({ code: "failed", message: "Account not found." }));
                return;
            }
        } else {
            if (tenant && user) {
                yield put(globalActions.unRegisterNotificationRequest(tenant.tenantId, user.userId));
            }
            yield put(actions.getUserAndTenantFailure({ code: "failed", message: "Get user failed" }));
        }
    } catch (error) {
        if (tenant && user) {
            yield put(globalActions.unRegisterNotificationRequest(tenant.tenantId, user.userId));
        }
        let errorObject = errorHandler(error, "Get", "user and tenant");
        yield put(actions.getUserAndTenantFailure(errorObject));
    }
}

export function* userLogout() {
    try {
        let tenant = yield select(getTenant);
        let user = yield select(getLoggedInUser);
        yield call(signOut);
        yield put(globalActions.unRegisterNotificationRequest(tenant.tenantId, user.userId));
        yield put(actions.userLogoutSuccess());
        //Reload the page after logout
        window.location.href = "/";
    } catch (error) {
        yield put(actions.userLogoutSuccess());
    }
}

export function* changeTemporaryPassword({ email, oldPassword, newPassword }) {
    try {
        let signInResult = yield call(signIn, { username: email, password: oldPassword });
        if (signInResult.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
            yield call(confirmSignIn, { challengeResponse: newPassword });
            yield put(actions.changeTemporaryPasswordSuccess("success"));
        } else {
            yield put(actions.changeTemporaryPasswordSuccess("success"));
        }
    } catch (error) {
        let errorObject = errorHandler(error, "Change", "temporary password");
        yield put(actions.changeTemporaryPasswordFailure(errorObject));
    }
}

export function* sendEmailVerificationLink({ email }) {
    try {
        yield call(resendSignUpCode, { username: email });
        yield put(actions.sendEmailVerificationLinkSuccess(true));
    } catch (error) {
        let errorObject = errorHandler(error, "Send", "email verification link.");
        yield put(actions.sendEmailVerificationLinkFailure(errorObject));
    }
}

export function* adminForceUserLogoutRequest({ message }) {
    try {
        let currentUser = yield call(getCurrentUser);
        let userAttributes = yield call(fetchUserAttributes);
        if (currentUser) {
            if (userAttributes["custom:tenantId"] === message.tenantId && currentUser.username === message.userId) {
                yield call(userLogout);
            }
        }
    } catch (error) {}
}

export function* watchUserLoginRequest(api, { params }) {
    yield call(userLogin, api, params);
}

export function* watchUserReauthRequest(api, { params }) {
    yield call(userReauth, api, params);
}

export function* watchGetUserAndTenantRequest(api, params) {
    yield call(getUserAndTenant, api);
}

export function* watchUserLogoutRequest(api, params) {
    yield call(userLogout);
}

export function* watchChangeTemporaryPasswordRequest(api, { params }) {
    yield call(changeTemporaryPassword, params);
}

export function* watchSendEmailVerificationLinkRequest(api, { params }) {
    yield call(sendEmailVerificationLink, params);
}

export function* watchAdminForceUserLogoutRequest(api, { params }) {
    yield call(adminForceUserLogoutRequest, params);
}

export default function* ({ api }) {
    yield takeEvery(actions.USER_LOGIN_REQUEST, watchUserLoginRequest, api);
    yield takeLatest(actions.USER_REAUTH_REQUEST, watchUserReauthRequest, api);
    yield takeEvery(actions.USER_LOGOUT_REQUEST, watchUserLogoutRequest, api);
    yield takeEvery(actions.GET_USER_AND_TENANT_REQUEST, watchGetUserAndTenantRequest, api);
    yield takeEvery(actions.CHANGE_TEMPORARY_PASSWORD_REQUEST, watchChangeTemporaryPasswordRequest, api);
    yield takeEvery(actions.SEND_EMAIL_VERIFICATION_LINK_REQUEST, watchSendEmailVerificationLinkRequest, api);
    yield takeEvery(globalActions.ADMIN_FORCE_USER_LOGOUT, watchAdminForceUserLogoutRequest, api);
}
